import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Address } from 'viem';

import { config } from '../config.ts';
import { shortAddr } from '../lib/format.ts';
import { enableSession, revokeSession } from '../lib/session.ts';
import { errorMessage } from '../lib/tx.ts';
import { connectLocal, connectWallet, discoverWallets, localAccounts, type Connected, type WalletInfo } from '../lib/wallet.ts';
import { useApp, type Page } from '../state.tsx';
import { copyText, useDialogFocus, useEscape } from './ui.tsx';

const NAV: { page: Page; label: string }[] = [
  { page: 'trade', label: 'Trade' },
  { page: 'portfolio', label: 'Portfolio' },
  { page: 'house', label: 'House' },
  { page: 'papurr', label: 'Papurr' },
  { page: 'stats', label: 'Stats' },
  { page: 'docs', label: 'Docs' },
  { page: 'leaderboard', label: 'Leaderboard' },
];

export function Header() {
  const { route, go, wallet, openFunds, connectOpen, openConnect, openTour, setWallet, toast } = useApp();
  const closeConnect = useCallback(() => openConnect(false), [openConnect]);
  return (
    <header className="topbar">
      <a className="brand" href="#/trade" onClick={() => go({ page: 'trade' })} aria-label={config.appName}>
        {/* the wordmark per theme ("papurr" is white on night, ink on day), the cat alone on narrow screens */}
        <img className="brand-logo night" src="/brand/logo-night.png" alt={config.appName} width={564} height={92} />
        <img className="brand-logo day" src="/brand/logo-day.png" alt={config.appName} width={564} height={92} />
        <img className="brand-mark" src="/brand/logo-mark.png" alt={config.appName} width={99} height={99} />
      </a>
      <nav className="nav">
        {NAV.map((n) => (
          <a
            key={n.page}
            href={n.page === 'trade' ? '#/trade' : `#/${n.page}`}
            className={route.page === n.page ? 'is-on' : ''}
            data-tour={`nav-${n.page}`}
            onClick={(e) => {
              e.preventDefault();
              go({ page: n.page, doc: undefined });
            }}
          >
            {n.label}
          </a>
        ))}
      </nav>
      <span className="grow" />
      <div className="topbar-actions">
        <button className="text-btn icon-btn" onClick={() => openTour(true)} title="Quick tour of the app" aria-label="Quick tour of the app">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4.5" />
            <path d="M12 17.5h.01" />
          </svg>
        </button>
        <ThemeToggle />
        <button className="text-btn" data-tour="deposit" onClick={() => (wallet ? openFunds('deposit') : openConnect(true))}>
          Deposit
        </button>
        <button className="text-btn withdraw-btn" onClick={() => (wallet ? openFunds('withdraw') : openConnect(true))}>
          Withdraw
        </button>
        {wallet ? <AccountMenu /> : (
          <button className="btn btn-primary btn-sm" onClick={() => openConnect(true)}>
            Connect Wallet
          </button>
        )}
      </div>
      {connectOpen && (
        <ConnectDialog
          onClose={closeConnect}
          onError={(e) => toast({ kind: 'err', title: 'Connection failed', body: errorMessage(e) })}
          onConnected={(c) => {
            setWallet(c);
            openConnect(false);
          }}
        />
      )}
    </header>
  );
}

function AccountMenu() {
  const { wallet, session, setSession, setWallet, toast } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (!wallet) return null;

  async function toggleSession() {
    setBusy(true);
    try {
      if (session) {
        await revokeSession(wallet!.address, wallet!.client, session);
        setSession(undefined);
        toast({ kind: 'info', title: 'One-click trading revoked' });
      } else {
        setSession(await enableSession(wallet!.address, wallet!.client));
        toast({ kind: 'ok', title: 'One-click trading enabled' });
      }
    } catch (e) {
      toast({ kind: 'err', title: 'Session update failed', body: errorMessage(e) });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <div className="menu-anchor" ref={ref}>
      <button className="text-btn account-btn" onClick={() => setOpen((v) => !v)} title={session ? 'One-click trading on' : 'Each order asks the wallet'}>
        <span className={`dot ${session ? 'dot-on' : 'dot-off'}`} />
        {shortAddr(wallet.address)}
      </button>
      {open && (
        <div className="menu">
          <div className="menu-meta">
            {wallet.name}
            <br />
            <span className="num">{wallet.address}</span>
          </div>
          <button disabled={busy} onClick={toggleSession}>
            {busy ? 'Check your wallet…' : session ? 'Revoke one-click trading' : 'Enable one-click trading'}
          </button>
          <button onClick={() => copyText(wallet.address).then(() => setOpen(false))}>Copy address</button>
          <button
            onClick={() => {
              setWallet(undefined);
              setOpen(false);
            }}
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}

function ConnectDialog(p: { onClose: () => void; onError: (e: unknown) => void; onConnected: (c: Connected) => void }) {
  const [wallets, setWallets] = useState<WalletInfo[]>([]);
  const [devAccounts, setDevAccounts] = useState<Address[]>([]);
  const [busy, setBusy] = useState<string>();
  useEscape(p.onClose);
  const dialog = useDialogFocus();

  useEffect(() => discoverWallets(setWallets), []);
  useEffect(() => {
    if (config.isLocal) localAccounts().then((a) => setDevAccounts(a.slice(3, 8)), () => {});
  }, []);

  return createPortal(
    <div className="scrim" onClick={p.onClose}>
      <div className="modal" ref={dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Connect a wallet">
        <header className="modal-head">
          <h3>Connect Wallet</h3>
          <button className="x" onClick={p.onClose} aria-label="Close">
            ×
          </button>
        </header>
        {wallets.length === 0 && devAccounts.length === 0 && <p className="muted">No browser wallet found. Install one and reload.</p>}
        <ul className="wallet-list">
          {wallets.map((w) => (
            <li key={w.id}>
              <button
                disabled={!!busy}
                onClick={async () => {
                  setBusy(w.id);
                  try {
                    p.onConnected(await connectWallet(w));
                  } catch (e) {
                    p.onError(e);
                  } finally {
                    setBusy(undefined);
                  }
                }}
              >
                {w.icon ? <img src={w.icon} alt="" /> : <span className="wallet-glyph">◎</span>}
                <span>{w.name}</span>
                {busy === w.id && <span className="muted">check your wallet…</span>}
              </button>
            </li>
          ))}
          {devAccounts.map((a, i) => (
            <li key={a}>
              <button onClick={() => p.onConnected(connectLocal(a))}>
                <span className="wallet-glyph">⚒</span>
                <span>
                  Anvil account #{i + 3} <span className="num muted">{shortAddr(a)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

function ThemeToggle() {
  // night unless the visitor chose day
  const [night, setNight] = useState(() => {
    try {
      return localStorage.getItem('papurr:theme') !== 'day';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = night ? 'night' : 'day';
    try {
      localStorage.setItem('papurr:theme', night ? 'night' : 'day');
    } catch {
      // storage blocked: the choice lasts for this visit
    }
  }, [night]);
  return (
    <button className="text-btn icon-btn" onClick={() => setNight((v) => !v)} title={night ? 'Day mode' : 'Night mode'} aria-label="Toggle night mode">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
        {night ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        ) : (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        )}
      </svg>
    </button>
  );
}
